import { jwtDecode } from 'jwt-decode';
import { touch } from 'redux-form';
import { get, chain, debounce, isFunction } from 'lodash';

import APIClient from '../../common/services/APIClient';
import { actions as idleActions } from '../../common/services/ReduxIdleMonitor';
import dictionariesTypesEnum from '../../common/constants/dictionaries';
import { initFieldNaming } from '../../common/components/appConfig/actions';
import { SET_IS_SWITCHING_ROLES, SET_SWITCHING_ROLES_ERROR } from './reducer';
import { BASIC_PROVIDER_TYPE, COGNITO_PROVIDER_TYPE, PROVIDER_REFRESH_TOKEN_REQUEST } from '../../common/constants/authProvider';
import Auth from '../../common/resources/Auth';
import { getViewingPlazaId } from '../PlazaView/selectors';
import Plaza from '../../common/resources/Plaza';
import { REDIRECT_QUERY_PARAM_NAME } from '../../common/services/AuthenticationRules';
import { getCookie, removeCookie } from '../../common/cookies';

export const TOKEN_STORAGE_NAME = 'id_token';
export const LOGIN_REDIRECT_PATH_STORAGE_NAME = 'i2p2_login_redirect_path';
export const DOMESTIC_CUSTOMER_INFO = 'domestic_info';

export const loginRequest = () => ({
  type: 'LOGIN_REQUEST',
});

export const setRefreshToken = (refreshToken) => ({
  type: 'SET_REFRESH_TOKEN',
  refreshToken,
});

export const initUILabels = () => (dispatch, getState) => {
  const {
    login: {
      user: { customer_id },
    },
  } = getState();

  APIClient.get(`/dictionaries/${dictionariesTypesEnum.fieldNaming}`, { params: { page_token: 0, page_size: 999 } }).then((resp) => {
    const res = chain(resp.data.payloads)
      .filter((item) => item.customer_id === customer_id)
      .keyBy('key')
      .mapValues('value')
      .value();

    dispatch(initFieldNaming(res));
  });
};

export const loginSuccess = (token) => {
  const user = jwtDecode(token);

  return {
    type: 'LOGIN_SUCCESS',
    user,
    token,
  };
};

const handleLoginSuccess =
  (response, redirect = null) =>
  (dispatch) => {
    const authToken = response.headers.authorization;
    const refreshToken = get(response.headers, 'refresh-token', null);

    const redirectPath = new URL(window.location.href).searchParams.get(REDIRECT_QUERY_PARAM_NAME) || redirect;

    dispatch(loginSuccess(authToken));
    dispatch(setRefreshToken(refreshToken));
    dispatch(initUILabels());
    dispatch(idleActions.start());

    if (redirectPath) {
      localStorage.setItem(REDIRECT_QUERY_PARAM_NAME, redirectPath);
    }
  };

export const loginError = (text) => ({
  type: 'LOGIN_ERROR',
  error: text,
});

const handleLoginError = (dispatch, errorText401) => (error) => {
  if (error.response) {
    switch (error.response.status) {
      case 401:
        // Wrong credentials
        dispatch(loginError(errorText401));
        break;
      case 403:
        // User with non-existing role
        dispatch(loginError('You are not authorized to access IoT.ON™ platform.'));
        break;
      case 502:
      case 503:
        dispatch(loginError('Service temporarily unavailable'));
        break;
      default:
        dispatch(loginError('Something went wrong'));
    }
  } else {
    dispatch(loginError('Something went wrong'));
  }
};

export const loginViaCode = (code) => (dispatch) => {
  dispatch(loginRequest());
  dispatch(touch('login'));
  APIClient.get('auth/session', {
    params: {
      code,
    },
  })
    .then((response) => {
      const redirectPath = localStorage.getItem(LOGIN_REDIRECT_PATH_STORAGE_NAME);

      if (redirectPath) {
        localStorage.removeItem(LOGIN_REDIRECT_PATH_STORAGE_NAME);
      }

      handleLoginSuccess(response, redirectPath)(dispatch);
    })
    .catch(handleLoginError(dispatch, 'Auth code is invalid.'));
};

export const login = (creds) => (dispatch) => {
  dispatch(loginRequest());
  APIClient.post('auth/session', creds)
    .then((response) => handleLoginSuccess(response)(dispatch))
    .catch(handleLoginError(dispatch, 'Your username and password combination is invalid.'));
};

export const forgetPassword = (email) => () => APIClient.put('/profiles/reset-password-request', { ...email });

export const resetPassword = (data) => () => APIClient.put('/profiles/reset-password', { ...data });

export const resetTokenValidate = (token) => () => APIClient.post('/profiles/validate-token', { token });

let alreadyRefreshing = false;

const refreshTokenRequestError = () => ({
  type: 'REFRESH_TOKEN_REQUEST_ERROR',
});

const refreshTokenRequestSuccess = () => ({
  type: 'REFRESH_TOKEN_REQUEST_SUCCESS',
});

let alreadyLogout = false;

export const logout =
  (callback = () => {}) =>
  (dispatch) => {
    if (!alreadyLogout) {
      alreadyLogout = true;

      Auth.logout()
        .catch(() => {})
        .finally(() => {
          alreadyLogout = false;
          dispatch({ type: 'LOGOUT' });
          if (isFunction(callback)) callback();
        });
    }

    return alreadyLogout;
  };

const refreshTokenRequest = () => ({
  type: 'REFRESH_TOKEN_REQUEST',
});

export const refreshToken = () => (dispatch, getState) => {
  if (!alreadyRefreshing) {
    alreadyRefreshing = true;
    dispatch(refreshTokenRequest());
    const state = getState();
    const providerType =
      get(state, 'login.user.authProvider.type', 'BASIC') === BASIC_PROVIDER_TYPE ? BASIC_PROVIDER_TYPE : COGNITO_PROVIDER_TYPE;
    const refreshToken = get(state, 'login.refreshToken', null);
    const request = PROVIDER_REFRESH_TOKEN_REQUEST[providerType];
    const body = providerType !== BASIC_PROVIDER_TYPE ? { refreshToken } : null;

    APIClient[request.type](request.url, body, request.config)
      .then((resp) => {
        dispatch(refreshTokenRequestSuccess());
        dispatch(loginSuccess(resp.headers.authorization));
      })
      .catch((error) => {
        if (error.response) {
          dispatch(logout());
          dispatch(refreshTokenRequestError());
        }
      })
      .finally(() => (alreadyRefreshing = false));
  }

  return alreadyRefreshing;
};

export const logoutAndRedirect = (url, resp, refreshToken) => (dispatch) => {
  dispatch(
    logout(() => {
      window.location.href = `${url}/login/customer?Authorization=${resp.headers.authorization}&refreshToken=${refreshToken}`;
    }),
  );
};

export const redirectToSupervisor = () => async (dispatch, getState) => {
  try {
    const { customer_id, customer_url } = dispatch(getCookie(DOMESTIC_CUSTOMER_INFO));

    const {
      headers: { authorization },
    } = await APIClient.get(`/users/login/supervisor?url=https://${customer_url}&customerId=${customer_id}`);

    dispatch(
      logout(() => {
        window.location.href = `//${customer_url}/login/customer?Authorization=${authorization}`;
      }),
    );

    const {
      login: {
        user: { domain },
      },
    } = getState();

    dispatch(removeCookie(DOMESTIC_CUSTOMER_INFO, domain));
  } catch (e) {
    // error handler
  }
};

export const loginToCustomer = (search) => ({
  type: 'LOGIN_TO_CUSTOMER',
  search,
});

export const clearFetchingStatus = () => ({
  type: 'CLEAR_FETCHING_STATUS',
});

export const setTokenExpirationIntervalId = (id) => ({
  type: 'SET_TOKEN_EXPIRATION_INTERVAL_ID',
  id,
});

export const tokenExpirationCheck = (timestamp) => ({
  type: 'TOKEN_EXPIRATION_CHECK',
  timestamp,
});

export const removeRefreshToken = () => ({
  type: 'REMOVE_REFRESH_TOKEN',
});

export const sessionExpireDialog = () => ({
  type: 'SESSION_EXPIRE_DIALOG',
});

export const closeSessionExpireDialog = () => ({
  type: 'CLOSE_SESSION_EXPIRE_DIALOG',
});

export const clearTokenRefreshTimeout = () => ({
  type: 'CLEAR_TOKEN_REFRESH_TIMEOUT',
});

export const setIsSwitchingRoles = (payload) => ({
  type: SET_IS_SWITCHING_ROLES,
  payload,
});

export const setSwitchingRolesError = (text) => ({
  type: SET_SWITCHING_ROLES_ERROR,
  error: text,
});
const handleSwitchingRolesError = (dispatch, errorText401) => (error) => {
  if (error.response) {
    switch (error.response.status) {
      case 401:
        dispatch(setSwitchingRolesError(errorText401));
        break;
      case 403:
        dispatch(setSwitchingRolesError('You are not authorized to switch roles'));
        break;
      case 502:
      case 503:
        dispatch(setSwitchingRolesError('Service temporarily unavailable'));
        break;
      default:
        dispatch(setSwitchingRolesError('Something went wrong'));
    }
  } else {
    dispatch(setSwitchingRolesError('Something went wrong'));
  }
};

// send only one request if user selects multiple roles
const debounceHandleRoleSwitch = debounce((selectedRoles, dispatch, getState) => {
  dispatch(setIsSwitchingRoles(true));

  const fromEditor = window.location.pathname.includes('editor');
  const plazaId = getViewingPlazaId(getState());

  if (fromEditor) {
    Plaza.editingUnlock({ plazaId });
  }

  const uuid = get(getState(), 'login.user.uuid');

  APIClient.post('/profiles/switch-role', {
    selectedRoles,
    uuid,
  })
    .then((response) => {
      const authToken = response.headers.authorization;

      dispatch(loginSuccess(authToken));
      setTimeout(() => {
        window.location.href = '/';
      }, 1005);
    })
    .catch(handleSwitchingRolesError(dispatch, 'Auth code is invalid.'))
    .finally(() => dispatch(setIsSwitchingRoles(false)));
}, 1500);

export const handleRoleSwitch = (selectedRoles) => (dispatch, getState) => debounceHandleRoleSwitch(selectedRoles, dispatch, getState);
